import type { TFunction } from 'i18next';

import i18n from '@sola/locales/index.ts';
import { PaymentFrequencyType } from '@sola/schema/dotcom/proxy-api/transactions.ts';
import { CommitmentCodeEnum } from '@sola/schema/dotcom/shared/commitments.ts';
import {
  getCommitmentLabel,
  getCommitmentTitle,
  isFundCodeChildSponsorship,
} from '@sola/supporter-sdk/utils/commitments.ts';

import type { GivingItemWithCommitment } from '../../utils/giving-history.ts';
import { GivingTypeEnum } from '../../utils/giving-history.ts';
import { formatPledgeName } from '../../utils/pledges.ts';

export type GivingHistoryCardParts = {
  title: string;
  label?: string;
  rate?: number | string;
};

export const getTaxYearForBadge = (
  givingItem: GivingItemWithCommitment,
): number | undefined => {
  // Keep this aligned with the UTC timezone used by giving-history date headings.
  const recordYear = new Date(givingItem.recordDate).getUTCFullYear();

  return givingItem.taxYear !== null &&
    Number.isFinite(recordYear) &&
    givingItem.taxYear !== recordYear
    ? givingItem.taxYear
    : undefined;
};

/**
 * Translations to be used for gift commitments.
 */
const giftTranslations = {
  [CommitmentCodeEnum.ChildSponsorship]: {
    titleKey: 'my:commitments.title.sponsorship',
    labelKey: 'my:commitments.category.childSponsorship',
  },
  [CommitmentCodeEnum.BirthdayGift]: {
    titleKey: 'my:commitments.title.birthday',
    labelKey: 'my:commitments.category.giftCommitment',
  },
  [CommitmentCodeEnum.ChildGift]: {
    titleKey: 'my:commitments.title.child',
    labelKey: 'my:commitments.category.giftCommitment',
  },
  [CommitmentCodeEnum.FamilyGift]: {
    titleKey: 'my:commitments.title.family',
    labelKey: 'my:commitments.category.giftCommitment',
  },
  [CommitmentCodeEnum.FinalGift]: {
    titleKey: 'my:commitments.title.final',
    labelKey: 'my:commitments.category.giftCommitment',
  },
};

const addFrequencyLabel = (
  givingItem: GivingItemWithCommitment,
  baseLabel: string,
  t: TFunction<'my', undefined>,
) =>
  givingItem.commitmentFrequency &&
  givingItem.commitmentFrequency !== PaymentFrequencyType.OneTime &&
  !isFundCodeChildSponsorship(givingItem.fundCode as CommitmentCodeEnum) &&
  i18n.exists(
    `core:frequencies.${givingItem.commitmentFrequency.toLowerCase()}`,
  )
    ? `${baseLabel} (${t('core:frequencies.' + givingItem.commitmentFrequency.toLowerCase())})`
    : baseLabel;

export const getHistoryItemData = (
  givingItem: GivingItemWithCommitment,
  t: TFunction<'my', undefined>,
): GivingHistoryCardParts => {
  // TODO: PIF is adding pledgeId to giving history at some point - so we could map to pledge

  // Handle Gifts in Kind
  if (givingItem.stockGiftInKindDescription) {
    switch (givingItem.givingType) {
      case GivingTypeEnum.Stocks: {
        return {
          title: t('commitments.givingTypes.stocks.title', {
            tickerSymbol: givingItem.stockTickerSymbol,
          }),
          label: t('commitments.givingTypes.stocks.label'),
          rate: t('commitments.givingTypes.stocks.rate', {
            count: givingItem.quantity ? +givingItem.quantity : 0,
          }),
        };
      }
      default: {
        return {
          title: givingItem.stockGiftInKindDescription,
          rate: `${givingItem.fundDescription}`,
        };
      }
    }
  }

  const rate = givingItem.isRefund ? givingItem.amount * -1 : givingItem.amount;
  // Handle the rest of history items, even without a commitment we have data
  const refundText = t('my:commitments.refund');
  const translations =
    giftTranslations[givingItem.fundCode as keyof typeof giftTranslations];

  const baseTitle = translations
    ? t(translations.titleKey, {
        // Giving items can include a beneficiary preferred name
        childName: givingItem.beneficiaryPreferredName,
      })
    : (givingItem.fundDescription ?? '');

  const baseLabel = translations
    ? t(translations.labelKey)
    : t('commitments.category.fundCommitment');

  // Commitment associated with history item?
  if (givingItem.commitment) {
    const { commitment } = givingItem;
    const title = getCommitmentTitle(commitment, t) || baseTitle;
    const label = getCommitmentLabel(commitment, t) || baseLabel;
    return {
      title: givingItem.isRefund ? `${refundText} ${title}` : title,
      label: addFrequencyLabel(givingItem, label, t),
      rate,
    };
  }

  // Pledge associated with history item?
  if (givingItem.pledge) {
    const { pledge } = givingItem;
    const pledgeTitle = pledge.pledgeName
      ? formatPledgeName(pledge.pledgeName, t)
      : baseTitle;
    return {
      title: givingItem.isRefund ? `${refundText} ${pledgeTitle}` : pledgeTitle,
      label: t('my:commitments.pledges.subTitle'),
      rate,
    };
  }

  const title = givingItem.isRefund ? `${refundText} ${baseTitle}` : baseTitle;

  return {
    title,
    label: addFrequencyLabel(givingItem, baseLabel, t),
    rate,
  };
};
